import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { Device } from '@capacitor/device';
import { AppLanguage, DEFAULT_APP_LANGUAGE, LanguageService } from '@work-frontend-monorepo/work-ui/services';

/**
 * Sprache der SFG-App: grundsätzlich die Gerätesprache (nicht unterstützt → Deutsch). Im Seitenmenü lässt sie sich
 * pro Gerät übersteuern; die Übersteuerung bleibt lokal und gilt nicht für das work-office.
 */
@Injectable({
  providedIn: 'root',
})
export class AppLanguageService {
  private static readonly OVERRIDE_STORAGE_KEY = 'sfg-app-language-override';

  private readonly languageService = inject(LanguageService);
  private readonly document = inject(DOCUMENT);

  /** `null` = Gerätesprache. */
  public readonly languageOverride = signal<AppLanguage | null>(null);

  public async initialize(): Promise<void> {
    await this.languageService.initialize();
    this.languageOverride.set(this.languageService.readStorage(AppLanguageService.OVERRIDE_STORAGE_KEY));
    await this.applyLanguage();
  }

  /** Speichert die Übersteuerung und lädt die App neu, damit auch bereits gerenderte Datums-/Monatsnamen umstellen. */
  public async setLanguageOverride(language: AppLanguage | null): Promise<void> {
    this.languageService.writeStorage(AppLanguageService.OVERRIDE_STORAGE_KEY, language);
    this.languageOverride.set(language);
    await this.applyLanguage();
    this.document.defaultView?.location.reload();
  }

  private async applyLanguage(): Promise<void> {
    await this.languageService.use(this.languageOverride() ?? (await this.getDeviceLanguage()));
  }

  private async getDeviceLanguage(): Promise<AppLanguage> {
    try {
      const languageCode = await Device.getLanguageCode();
      return LanguageService.toAppLanguage(languageCode.value) ?? DEFAULT_APP_LANGUAGE;
    } catch (deviceLanguageException) {
      return DEFAULT_APP_LANGUAGE;
    }
  }
}
